/**
 * Occasions the theme toggle dresses its anglerfish for. Each has a window of calendar days in
 * the viewer's own time zone, the same every year; the first window that holds a day wins.
 */

export type OccasionId = 'halloween' | 'christmas' | 'newYear' | 'easter' | 'may17';

interface MonthDay {
    month: number;
    day: number;
}

type OccasionWindow =
    /** Inclusive; `from` after `to` wraps over the new year. */
    | { kind: 'fixed'; from: MonthDay; to: MonthDay }
    /** Inclusive, in days from Easter Sunday, which moves every year. */
    | { kind: 'easter'; fromDays: number; toDays: number };

interface Occasion {
    id: OccasionId;
    window: OccasionWindow;
}

function fixed(from: [number, number], to: [number, number]): OccasionWindow {
    return {
        kind: 'fixed',
        from: { month: from[0], day: from[1] },
        to: { month: to[0], day: to[1] },
    };
}

export const OCCASIONS: readonly Occasion[] = [
    { id: 'newYear', window: fixed([12, 31], [1, 1]) },
    { id: 'christmas', window: fixed([12, 1], [12, 26]) },
    { id: 'halloween', window: fixed([10, 24], [11, 1]) },
    { id: 'may17', window: fixed([5, 17], [5, 17]) },
    // Palm Sunday to Easter Monday.
    { id: 'easter', window: { kind: 'easter', fromDays: -7, toDays: 1 } },
];

export const OCCASION_IDS: readonly OccasionId[] = OCCASIONS.map((occasion) => occasion.id);

export function isOccasionId(value: unknown): value is OccasionId {
    return OCCASION_IDS.some((id) => id === value);
}

/** Easter Sunday of a Gregorian year, as month (1-12) and day (the anonymous Gregorian algorithm). */
export function easterSunday(year: number): MonthDay {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return { month, day };
}

// Whole days between two calendar dates; UTC so a daylight saving change cannot shift it.
function daysBetween(year: number, from: MonthDay, to: MonthDay): number {
    const start = Date.UTC(year, from.month - 1, from.day);
    const end = Date.UTC(year, to.month - 1, to.day);
    return Math.round((end - start) / 86_400_000);
}

function holds(window: OccasionWindow, year: number, today: MonthDay): boolean {
    if (window.kind === 'easter') {
        const offset = daysBetween(year, easterSunday(year), today);
        return offset >= window.fromDays && offset <= window.toDays;
    }
    const key = (date: MonthDay) => date.month * 100 + date.day;
    const [from, to, day] = [key(window.from), key(window.to), key(today)];
    return from <= to ? day >= from && day <= to : day >= from || day <= to;
}

/** The occasion of a moment's local calendar day, or null on an ordinary day. */
export function occasionOn(moment: Date): OccasionId | null {
    const today = { month: moment.getMonth() + 1, day: moment.getDate() };
    const year = moment.getFullYear();
    return OCCASIONS.find((occasion) => holds(occasion.window, year, today))?.id ?? null;
}
